/**
 * Desktop camera capture (getUserMedia). The stream is stopped as soon as the photo is taken or the dialog
 * closes; the picture exists only in memory until it is sent. Phones use the system camera via <input capture>.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export function CameraCapture({ onCapture, onClose }: { onCapture: (f: File) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  // Parents re-render often (Team Chat ticks every second); keep the latest callbacks in refs so the
  // camera stream is opened exactly once per dialog and never restarted by a parent re-render.
  const closeRef = useRef(onClose);
  const captureRef = useRef(onCapture);
  useLayoutEffect(() => {
    closeRef.current = onClose;
    captureRef.current = onCapture;
  });
  const [taking, setTaking] = useState(false);

  useEffect(() => {
    let alive = true;
    navigator.mediaDevices
      .getUserMedia({ video: { width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false })
      .then((s) => {
        if (!alive) return s.getTracks().forEach((t) => t.stop());
        stream.current = s;
        if (video.current) {
          video.current.srcObject = s;
          void video.current.play().catch(() => undefined);
        }
      })
      .catch((e: DOMException) =>
        setError(
          e.name === 'NotAllowedError'
            ? 'Kamera izni verilmedi. Tarayıcının adres çubuğundan kameraya izin verip tekrar deneyin.'
            : e.name === 'NotFoundError'
              ? 'Bu cihazda kamera bulunamadı.'
              : 'Kamera açılamadı.',
        ),
      );
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeRef.current();
    window.addEventListener('keydown', onKey);
    return () => {
      alive = false;
      window.removeEventListener('keydown', onKey);
      stream.current?.getTracks().forEach((t) => t.stop());
      stream.current = null;
    };
  }, []);

  function snap() {
    const v = video.current;
    if (!v || !v.videoWidth || taking) return;
    setTaking(true);
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d')?.drawImage(v, 0, 0);
    c.toBlob(
      (b) => {
        c.width = c.height = 0;
        stream.current?.getTracks().forEach((t) => t.stop());
        if (b) captureRef.current(new File([b], 'kamera.jpg', { type: 'image/jpeg' }));
        closeRef.current();
      },
      'image/jpeg',
      0.86,
    );
  }

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="Kamerayla fotoğraf çek">
      <div className="overlay-card camera">
        {error ? (
          <p className="error-text" role="alert">
            {error}
          </p>
        ) : (
          <video
            ref={video}
            playsInline
            muted
            aria-label="Kamera önizlemesi"
            // Enable "Çek" only once real frames with a size have arrived (otherwise a click would do nothing).
            onLoadedData={(e) => e.currentTarget.videoWidth > 0 && setReady(true)}
            onResize={(e) => e.currentTarget.videoWidth > 0 && setReady(true)}
          />
        )}
        <div className="row-actions">
          {!error && (
            <button
              className="btn primary"
              type="button"
              onClick={snap}
              disabled={!ready || taking}
              autoFocus
            >
              📷 Çek ve gönder
            </button>
          )}
          <button className="btn ghost" type="button" onClick={() => closeRef.current()}>
            Vazgeç
          </button>
        </div>
      </div>
    </div>
  );
}
